import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, Moon, Pause, Play, SlidersHorizontal, Sun } from "lucide-react";

import { useBoardSession } from "../../application/useBoardSession";
import { useBroadcastBlsChannel } from "../../application/useBroadcastBlsChannel";
import { useBlsHaptics } from "../../application/useBlsHaptics";
import { useEmdrSets } from "../../application/useEmdrSets";
import { useBoardRelay } from "../../application/useBoardRelay";
import { useBlsSettings } from "../../application/useBlsControls";
import type { BlsState } from "../../domain/blsChannel";
import { patientScreenPath } from "../../domain/boardPaths";
import BlsStimulus from "../components/BlsStimulus";
import BoardHistory from "../components/board/BoardHistory";
import BoardRating from "../components/board/BoardRating";
import BoardSettings from "../components/board/BoardSettings";
import BoardStage from "../components/board/BoardStage";
import PatientOutputs from "../components/board/PatientOutputs";
import PatientBlsView from "../components/PatientBlsView";
import ShareBoardDialog from "../components/ShareBoardDialog";

const THEME_KEY = "alchely-emdr-board-theme";

/** The board's own light/dark choice, remembered on this device. Dark unless chosen. */
function useBoardTheme() {
    const [light, setLight] = useState(() => {
        try {
            return window.localStorage.getItem(THEME_KEY) === "light";
        } catch {
            return false;
        }
    });
    const toggle = () =>
        setLight((current) => {
            const next = !current;
            try {
                window.localStorage.setItem(THEME_KEY, next ? "light" : "dark");
            } catch {
                // Private mode: the choice lasts this visit.
            }
            return next;
        });
    return { light, toggle };
}

const PREVIEW_SETTINGS: BlsState["settings"] = {
    mode: "both",
    path: "horizontal",
    cyclesPerMinute: 40,
    dotSize: "medium",
    dotColor: "white",
    tactile: false,
};

/**
 * Bilateral stimulation outside a call: in the consulting room.
 *
 * Opened from a session's tools in a tab of its own, like the constellation
 * board, or from a dock, which starts a new session for it.
 *
 * Laid out as a console for the work rather than the call's side panel
 * stretched: where the patient sees the dot, then the set (state, passes, the
 * one preview, large buttons), its ratings when it is paused, the session's
 * sets so far — and the stimulus settings beside them. Space starts and
 * pauses. Three ways to show the patient the stimulus:
 *
 * - **This screen**: the stimulus takes the whole screen, with a small bar to
 *   pause or bring the controls back — a laptop turned towards the patient.
 * - **A second window**: the patient's screen opens in its own window, for a
 *   monitor or a tablet facing them, and this one keeps the controls. The two
 *   talk through the browser (`BroadcastChannel`); nothing leaves the machine.
 * - **A link** for a patient somewhere else — on the phone, on another video
 *   platform. Only then does the board touch the network: it relays the set
 *   through a mailbox on our server, and the patient's device follows it.
 */
export default function EmdrBoardPage() {
    const { t } = useTranslation();
    const { sessionId = "" } = useParams<{ sessionId: string }>();
    const { session, individual } = useBoardSession(sessionId);
    const remote = useBoardRelay(sessionId);
    const channel = useBroadcastBlsChannel(sessionId, "therapist", remote.relay);
    const [shareOpen, setShareOpen] = useState(false);
    const [finishing, setFinishing] = useState(false);
    const navigate = useNavigate();
    const recorder = useEmdrSets(individual ? sessionId : null);
    const [presenting, setPresenting] = useState(false);
    // On this screen the therapist's device is the patient's device too.
    const haptics = useBlsHaptics({ state: channel.state, cyclesNow: channel.cyclesNow, enabled: presenting });

    useEffect(() => {
        if (!presenting) return;
        void document.documentElement.requestFullscreen?.().catch(() => undefined);
        return () => {
            if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => undefined);
        };
    }, [presenting]);

    const { settings, change } = useBlsSettings(channel);
    // The console only: presenting, and the stimulus field, stay dark.
    const theme = useBoardTheme();

    // The second-monitor window, watched so the board can say it is open.
    const windowRef = useRef<Window | null>(null);
    const [windowOpen, setWindowOpen] = useState(false);
    useEffect(() => {
        if (!windowOpen) return;
        const id = window.setInterval(() => {
            if (!windowRef.current || windowRef.current.closed) setWindowOpen(false);
        }, 1000);
        return () => window.clearInterval(id);
    }, [windowOpen]);

    const openPatientScreen = () => {
        windowRef.current = window.open(patientScreenPath(sessionId), `emdr-patient-${sessionId}`, "popup,width=1280,height=800");
        setWindowOpen(Boolean(windowRef.current));
    };

    // The number the next rated set will be saved under — the server numbers
    // rated sets only — and none until the saved ones have loaded.
    const setNumber = individual && recorder.loading ? null : recorder.sets.length + 1;
    const startSet = useCallback(() => channel.start(settings), [channel, settings]);

    // Space drives the set, as on any player — unless the key is someone
    // else's, typing a note or on a focused control.
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.code !== "Space" || presenting) return;
            const target = event.target;
            if (target instanceof Element && target.closest("input, textarea, select, button, [role=radio], [contenteditable=true]")) return;
            event.preventDefault();
            const { state } = channel;
            if (!state) startSet();
            else if (state.running) channel.pause();
            else channel.resume();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [channel, presenting, startSet]);

    /**
     * The end of the EMDR work: ratings still in the form go into the session,
     * the set comes down on every screen showing it, and the board gives way
     * to the session — on its EMDR tab, where the sets just saved are.
     */
    const finish = async () => {
        if (finishing) return;
        setFinishing(true);
        await recorder.flushPending?.().catch(() => undefined);
        channel.stop();
        navigate(individual ? `/session/${sessionId}?tab=emdr` : `/session/${sessionId}`);
    };

    const openShare = () => {
        setShareOpen(true);
        void remote.share();
    };

    const preview: BlsState = channel.state ?? { settings: PREVIEW_SETTINGS, running: false, cycles: 0, atEpochMs: 0 };

    if (presenting) {
        return (
            <div className="room-surface fixed inset-0 z-50 flex flex-col bg-room-base text-room-ink" data-testid="emdr-board-presenting">
                {channel.state ? (
                    <PatientBlsView channel={channel} haptics={haptics} />
                ) : (
                    <>
                        <BlsStimulus state={preview} cyclesNow={channel.cyclesNow} className="size-full" />
                        {/* Started from the controls, where the speed and the
                            path were chosen — not from here with defaults. */}
                        <p role="status" className="absolute inset-x-0 bottom-6 text-center text-sm text-room-ink-muted">
                            {t("emdr.board.startFromControls")}
                        </p>
                    </>
                )}
                <div className="absolute right-3 top-3 flex gap-2">
                    {channel.state && !channel.state.running && (
                        <button type="button" onClick={channel.resume} className="room-control room-control-primary">
                            <Play size={16} aria-hidden="true" />
                            <span>{t("emdr.resume")}</span>
                        </button>
                    )}
                    {channel.state?.running && (
                        <button type="button" onClick={channel.pause} className="room-control">
                            <Pause size={16} aria-hidden="true" />
                            <span>{t("emdr.pause")}</span>
                        </button>
                    )}
                    <button type="button" onClick={() => setPresenting(false)} className="room-control">
                        <SlidersHorizontal size={16} aria-hidden="true" />
                        <span>{t("emdr.board.showControls")}</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            translate="no"
            className={`room-surface flex w-full flex-col bg-room-base text-room-ink ${theme.light ? "room-light" : ""}`}
            data-testid="emdr-board"
        >
            <header className="shrink-0 border-b border-room-line-soft">
                <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3">
                <h1 className="flex min-w-0 items-center gap-2">
                    {/* The same mark, treated the same way, as the AlchelyMeeting
                        header: white on the dark room. */}
                    <img
                        src="/images/logo/icon.png"
                        alt="Alchely"
                        width={26}
                        height={26}
                        // White on the dark room; as drawn on the light one.
                        className={`shrink-0 rounded-md ${theme.light ? "" : "brightness-0 invert"}`}
                    />
                    <span className="flex min-w-0 flex-col">
                        <span className="text-sm font-semibold">{t("emdr.console.title")}</span>
                        {session?.title && <span className="truncate text-xs text-room-ink-muted">{session.title}</span>}
                    </span>
                </h1>
                <div className="flex shrink-0 items-center gap-2">
                <button
                    type="button"
                    onClick={theme.toggle}
                    aria-pressed={theme.light}
                    aria-label={t(theme.light ? "emdr.console.theme.dark" : "emdr.console.theme.light")}
                    title={t(theme.light ? "emdr.console.theme.dark" : "emdr.console.theme.light")}
                    className="room-control size-11 px-0"
                >
                    {theme.light ? <Moon size={16} aria-hidden="true" /> : <Sun size={16} aria-hidden="true" />}
                </button>
                <button
                    type="button"
                    onClick={() => void finish()}
                    disabled={finishing}
                    className="room-control room-control-primary disabled:opacity-50"
                >
                    {finishing ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <CheckCircle2 size={16} aria-hidden="true" />}
                    <span>{t("emdr.board.finish")}</span>
                </button>
                </div>
                </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {/* On a phone the settings come right after the set, before the
                    history; on a wide screen they stand beside both. */}
                <div className="mx-auto grid w-full max-w-6xl gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
                    <div className="flex min-w-0 flex-col gap-4 lg:col-start-1">
                        <PatientOutputs
                            onPresentHere={() => setPresenting(true)}
                            onOpenWindow={openPatientScreen}
                            windowOpen={windowOpen}
                            onShare={openShare}
                            remote={{ shared: Boolean(remote.shareUrl), present: remote.patientPresent }}
                        />
                        <BoardStage channel={channel} settings={settings} setNumber={setNumber} onStart={startSet} />
                        {individual ? (
                            <BoardRating state={channel.state} recorder={recorder} setNumber={setNumber} />
                        ) : (
                            session && <p className="text-xs text-room-ink-muted">{t("emdr.console.noRatings")}</p>
                        )}
                    </div>
                    <aside className="lg:sticky lg:top-0 lg:col-start-2 lg:row-span-2 lg:row-start-1">
                        <BoardSettings settings={settings} change={change} patientControllers={channel.patientControllers} />
                    </aside>
                    {individual && (
                        <div className="min-w-0 lg:col-start-1">
                            <BoardHistory recorder={recorder} />
                        </div>
                    )}
                </div>
            </div>

            <ShareBoardDialog isOpen={shareOpen} onClose={() => setShareOpen(false)} remote={remote} />
        </div>
    );
}
